import React, { useEffect, useState } from 'react';
import { customersAPI } from '../lib/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { formatCurrency, formatDate } from '../lib/utils';
import { Loader2, FileText, CheckCircle2, Circle, Eye, PoundSterling } from 'lucide-react';

const Section = ({ title, children, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">{title}</h3>
    {children}
  </div>
);

const PaidPill = ({ paid }) => (
  <Badge variant="outline" className={paid ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-amber-100 text-amber-700 border-amber-200'}>
    {paid ? 'Paid' : 'Due'}
  </Badge>
);

export const PortalPreviewPanel = ({ customerId, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !customerId) return;
    setLoading(true);
    customersAPI.portalPreview(customerId)
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, customerId]);

  const p = data?.project || {};
  const pay = data?.payments || {};

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="portal-preview-panel">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-emerald-600" /> Customer Portal Preview
          </SheetTitle>
          <SheetDescription>
            Read-only view of exactly what {p.first_name ? `${p.first_name} ${p.last_name}` : 'this customer'} sees on their portal.
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : !data ? (
          <p className="text-sm text-slate-400 py-12 text-center">Could not load portal preview.</p>
        ) : (
          <div className="mt-6 space-y-7">
            {/* Status / progress */}
            <Section title="Project status" testid="preview-status">
              <div className="flex items-center justify-between">
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200" data-testid="preview-status-badge">{p.status_display}</Badge>
                <span className="text-xs text-slate-400">{data.progress}% complete</span>
              </div>
              <Progress value={data.progress} className="h-2" />
              {(p.confirmed_install_date || p.provisional_install_date) && (
                <p className="text-xs text-slate-500">
                  {p.confirmed_install_date ? 'Install confirmed: ' : 'Provisional install: '}
                  {formatDate(p.confirmed_install_date || p.provisional_install_date)}
                </p>
              )}
            </Section>

            {/* System summary */}
            <Section title="Your system" testid="preview-system">
              <div className="rounded-lg border border-slate-200 p-3 text-sm space-y-1">
                {p.system_size ? <p className="text-slate-700">{p.system_size} kW system</p> : null}
                {p.panel_manufacturer ? <p className="text-slate-500">{p.panel_quantity ? `${p.panel_quantity}× ` : ''}{p.panel_manufacturer} panels</p> : null}
                {p.inverter_manufacturer ? <p className="text-slate-500">{p.inverter_manufacturer} inverter</p> : null}
                {p.battery_manufacturer ? <p className="text-slate-500">{p.battery_manufacturer} battery</p> : null}
                {p.system_total_cost ? <p className="text-slate-900 font-medium pt-1">{formatCurrency(p.system_total_cost)}</p> : null}
              </div>
            </Section>

            {/* Payments */}
            <Section title="Payments" testid="preview-payments">
              <div className="space-y-2">
                {pay.deposit_amount ? (
                  <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                    <div>
                      <p className="text-sm font-medium text-slate-800 flex items-center gap-1.5"><PoundSterling className="w-4 h-4 text-slate-400" />Deposit</p>
                      <p className="text-xs text-slate-500">{formatCurrency(pay.deposit_amount)}{pay.deposit_paid_date ? ` · paid ${formatDate(pay.deposit_paid_date)}` : ''}</p>
                    </div>
                    <PaidPill paid={!!pay.deposit_paid_date} />
                  </div>
                ) : null}
                {pay.final_bill_amount ? (
                  <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                    <div>
                      <p className="text-sm font-medium text-slate-800 flex items-center gap-1.5"><PoundSterling className="w-4 h-4 text-slate-400" />Final balance</p>
                      <p className="text-xs text-slate-500">{formatCurrency(pay.final_bill_amount)}{pay.final_bill_paid_date ? ` · paid ${formatDate(pay.final_bill_paid_date)}` : ''}</p>
                    </div>
                    <PaidPill paid={!!pay.final_bill_paid_date} />
                  </div>
                ) : null}
                {!pay.deposit_amount && !pay.final_bill_amount && <p className="text-sm text-slate-400 italic">No payments set up yet.</p>}
              </div>
            </Section>

            {/* Invoices */}
            <Section title={`Invoices (${data.invoices.length})`} testid="preview-invoices">
              {data.invoices.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No invoices yet.</p>
              ) : data.invoices.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3" data-testid={`preview-invoice-${inv.id}`}>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{inv.number || 'Invoice'}{inv.invoice_type ? <span className="text-xs text-slate-400 ml-2 capitalize">{inv.invoice_type.replace('_', ' ')}</span> : null}</p>
                    <p className="text-xs text-slate-500">{formatCurrency(inv.amount)}</p>
                  </div>
                  <PaidPill paid={inv.paid} />
                </div>
              ))}
            </Section>

            {/* Quotes */}
            {data.quotes.length > 0 && (
              <Section title={`Quotes (${data.quotes.length})`} testid="preview-quotes">
                {data.quotes.map((q) => (
                  <div key={q.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{q.number || 'Quote'}</p>
                      <p className="text-xs text-slate-500">{formatCurrency(q.amount)}</p>
                    </div>
                    <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200">{q.status}</Badge>
                  </div>
                ))}
              </Section>
            )}

            {/* Documents */}
            <Section title={`Documents (${data.documents.length})`} testid="preview-documents">
              {data.documents.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No documents shared yet.</p>
              ) : data.documents.map((d) => (
                <div key={d.id} className="flex items-center gap-2 rounded-lg border border-slate-200 p-3 text-sm text-slate-700">
                  <FileText className="w-4 h-4 text-slate-400" /> {d.name}
                </div>
              ))}
            </Section>

            {/* Checklist */}
            {data.checklist?.total > 0 && (
              <Section title={`Installation checklist (${data.checklist.completed}/${data.checklist.total})`} testid="preview-checklist">
                <div className="space-y-1.5">
                  {data.checklist.items.map((it, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      {it.completed ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Circle className="w-4 h-4 text-slate-300" />}
                      <span className={it.completed ? 'text-slate-700' : 'text-slate-400'}>{it.title}</span>
                    </div>
                  ))}
                </div>
              </Section>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default PortalPreviewPanel;
